import { Component, Inject } from '@angular/core';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { Vessel } from 'icreate/common/models/vessel';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { lastValueFrom } from 'rxjs';
import _ from 'lodash';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommunicationProtocolService } from '@service/communication-protocol.service';
import { CommunicationProtocol, ProtocolType } from 'icreate/common/models/communication-protocol';
import { CommunicationProtocolValidator } from 'icreate/common/validators/communication-protocol/communication-protocol-validator';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { CommunicationType, communicationTypes } from 'icreate/common/models/fu-unit';

@Component({
    selector: 'app-communication-protocol-dialog',
    templateUrl: './communication-protocol-dialog.component.html',
    styleUrl: './communication-protocol-dialog.component.css',
    standalone: false,
})
export class CommunicationProtocolDialogComponent {
    readonly TYPE_OPTIONS: SimpleDropdownOption[] = [
        { value: ProtocolType.POLLING, string: 'communication-protocols.type.polling' },
        { value: ProtocolType.RECEIVER, string: 'communication-protocols.type.receiver' },
        { value: ProtocolType.TRANSMITER, string: 'communication-protocols.type.transmiter' },
        { value: ProtocolType.RESPONDER, string: 'communication-protocols.type.responder' },
    ];

    readonly BOARD_TYPE_OPTIONS: SimpleDropdownOption[] = [
        { value: communicationTypes.serial, string: 'dlg.serial' },
        { value: communicationTypes.lan, string: 'dlg.lan' },
    ];

    title: string;
    selectedType: ProtocolType;
    selectedBoardType: CommunicationType;
    protocolList: CommunicationProtocol[];
    vessel: Vessel;

    isEditing: boolean = false;
    isSubmiting: boolean = false;
    initialType;
    initialTitle: string;
    validationErrors: ValidationErrorMap = {};
    hasError: boolean;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            protocolList: CommunicationProtocol[];
            vessel: Vessel;
            protocol: CommunicationProtocol;
        },
        private dialogRef: MatDialogRef<CommunicationProtocolDialogComponent>,
        private communicationProtocolService: CommunicationProtocolService,
        private dialog: MatDialog
        // private graphService: GraphsService
    ) {}

    ngOnInit(): void {
        if (this.data.protocolList) {
            this.protocolList = this.data.protocolList;
        }
        if (this.data.vessel) {
            this.vessel = this.data.vessel;
        }

        if (this.data.protocol) {
            this.isEditing = true;
            this.title = this.data.protocol.name;
            this.selectedType = this.data.protocol.type;
            this.selectedBoardType = this.data.protocol.boardType;
        }

        this.initialTitle = this.title;
        this.dialogHasErrors();
    }

    async create() {
        if (!this.isEditing && this.isCreateDisabled) return;
        else if (this.isEditing && this.isEditDisabled) return;

        const newProtocol: CommunicationProtocol = {
            name: this.title,
            type: this.selectedType,
            boardType: this.selectedBoardType,
        };

        this.dialogRef.close(newProtocol);
    }

    get isCreateDisabled() {
        return this.hasError || (!this.title && this.title != '') || !this.selectedType || !this.selectedBoardType;
    }

    get isEditDisabled() {
        return (
            this.hasError ||
            (this.title == this.data.protocol.name &&
                this.selectedType == this.data.protocol.type &&
                this.selectedBoardType == this.data.protocol.boardType)
        );
    }

    dialogHasErrors() {
        const protocol = {
            title: this.title,
            type: this.selectedType,
            boardType: this.selectedBoardType,
        };

        const errors = getValidationErrors(CommunicationProtocolValidator(), protocol);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        let isRepeated = false;
        if (this.protocolList && this.protocolList.length > 0) {
            this.protocolList.forEach(protocol => {
                if (this.data.protocol?.id !== protocol.id && protocol.name === this.title && !this.isSubmiting) {
                    isRepeated = true;
                }
            });
        }

        if (isRepeated) {
            mappedErrors['title'] = { message: 'communication-protocols.dialog.title.error.repeated' };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        const propertiesToCheck = Object.keys(protocol);
        for (const property of propertiesToCheck) {
            if (property in mappedErrors) {
                this.hasError = true;
                return;
            }
        }

        this.hasError = false;
    }

    async cancel() {
        const isEqual = _.isEqual(this.initialTitle, this.title);

        if (!isEqual && this.isEditing) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.SIMPLE,
                    title: 'communication-protocols.dialog.cancel.title',
                    message: 'communication-protocols.dialog.cancel.subLabel',
                    discardButton: 'dlg.cancel',
                    saveButton: 'dlg.confirm',
                    width: '452px',
                },
                disableClose: true,
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.SAVE) {
                this.dialogRef.close();
            }
        } else {
            this.dialogRef.close();
        }
    }

    onInputChange(event: { name: string; value: string }) {
        this.title = event.value;
        this.dialogHasErrors();
    }

    handleTypeChanged(event) {
        this.selectedType = event.value;
        this.dialogHasErrors();
    }

    handleBoardTypeChanged(event) {
        this.selectedBoardType = event.value;
        this.dialogHasErrors();
    }
}
